import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Control,
  UseFormWatch,
  UseFormSetValue,
  Controller,
  UseFormGetValues,
} from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { Chip } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import moment from 'moment';
import AppSwitch from '../../../../../../components/common/AppSwitch';
import AppTextInput from '../../../../../../components/common/AppTextInput';
import AppMUIAutocomplete from '../../../../../../components/common/AppMUIAutoComplete';
import AppButton from '../../../../../../components/common/AppButton';
import MUIIconCheckboxSelect from '../../../../../../components/common/MUIIconCheckboxSelect';
import BranchTreeAutocomplete from '../../../../../../components/common/BranchTreeAutocomplete';
import { DateInput } from '../../../../../operations/components/Modals/CustomTiming/components/DateInput';
import CustomTiming from '../../../../../operations/components/Modals/CustomTiming';
import { SVG } from '../../../../../../constants/assetLists';
import { useClientsContext } from '../../../../../../context/clients/ClientsContextProvider';
import type {
  ClientDataType,
  SubBranchData,
} from '../../../../../../context/clients/ClientsContextProvider';
import {
  CustomTimingFormValues,
  useCustomTiming,
} from '../../../../../../context/CustomTimingContextProvider';
import { handleGetAllDepartmentsService } from '../../../../../../services/operations/departments';
import { handleGetAllUsersService } from '../../../../../../services/users';
import { ApiResponse } from '../../../../../../services/axiosInstance';
import { UsersListResponse } from '../../../../../users/components/UserScreens/users';
import QUERY_KEYS from '../../../../../../constants/queryKeys';
import { getCorrectPath, DAYS, getRoleTypeName } from '../../../../../../utils';
import { ICONS } from '../../../../../users/data';
import { FormDataType } from '../../types';
import { TIMES } from '../../../../../operations/components/Modals/AddAnnouncement/components/Time';

type Props = {
  control: Control<FormDataType>;
  watch: UseFormWatch<FormDataType>;
  setValue: UseFormSetValue<FormDataType>;
  getValues: UseFormGetValues<FormDataType>;
  errors: any;
  // copyMode?: boolean;
  // copyClientId?: string;
  // setCopyClientId?: React.Dispatch<React.SetStateAction<string>>;
  // copyBranchIds?: string[];
  // setCopyBranchIds?: React.Dispatch<React.SetStateAction<string[]>>;
};

const TIMING_TYPES = [
  { value: 'single', labelHe: 'חד פעמי', labelEn: 'Single-use form' },
  { value: 'scheduled', labelHe: 'מתוזמן', labelEn: 'Scheduled' },
];

const SCHEDULING_TYPES = [
  { value: 'default', key: 'default', title: 'ללא תזמון - דיפולט' },
  { value: 'allDay', key: 'all_day', title: 'לכל היום' },
  {
    value: 'sunday',
    key: 'by_day_and_time',
    title: 'לפי תאריך ושעה (ביום ראשון בשעה 08:00)',
  },
  {
    value: 'sundayToTuesday',
    key: 'between_days_and_hours',
    title: 'בין ימים ושעות(בין ראשון לשלישי בין 08:00 - 09:00)',
  },
  {
    value: 'everyday',
    key: 'daily_at_fixed_time',
    title: 'תזמון כל יום בשעה קבועה',
  },
  { value: 'custom', key: 'custom', title: 'תזמון מותאם' },
];

function SettingsTab({
  control,
  watch,
  setValue,
  getValues,
  errors,
  // copyMode = false,
  // copyClientId,
  // setCopyClientId,
  // copyBranchIds,
  // setCopyBranchIds,
}: Props) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'heb';

  const { clearCustomTimingData, customTimingData, setCustomTimingData } =
    useCustomTiming();
  const [showCustomTimingDialog, setShowCustomTimingDialog] = useState(false);

  const { allClients, selectedClient, selectedLocation } = useClientsContext();

  const branchIds = watch('settings.branchIds');
  const subBranchIds = watch('settings.subBranchIds');

  const isClientSelected = selectedLocation?.isClient;
  const isBranchSelected = selectedLocation?.isBranch;
  const isSubBranchSelected = selectedLocation?.isSubBranch;

  const effectiveBranchId: string[] = isClientSelected
    ? (branchIds ?? [])
    : isBranchSelected && branchIds?.length
      ? branchIds
      : isSubBranchSelected && selectedLocation?.branchId
        ? [selectedLocation.branchId]
        : [];
  const effectiveSubBranchId: string[] =
    isClientSelected || isBranchSelected
      ? (subBranchIds ?? [])
      : isSubBranchSelected && selectedLocation?.id
        ? [selectedLocation.id]
        : [];
  const effectiveBranchKey = effectiveBranchId.join('|');
  const effectiveSubBranchKey = effectiveSubBranchId.join('|');

  const { data: allDepartments } = useQuery({
    queryKey: [
      QUERY_KEYS.GET_ALL_DEPARTMENTS,
      selectedClient?._id,
      effectiveBranchId,
      effectiveSubBranchId,
    ],
    queryFn: () =>
      handleGetAllDepartmentsService({
        clientId: selectedClient?._id,
        ...(effectiveBranchId.length > 0 && { branchId: effectiveBranchId }),
        ...(effectiveSubBranchId.length > 0 && {
          subBranchId: effectiveSubBranchId,
        }),
      }),
    enabled: !!selectedClient?._id,
  });

  const { data: allUsers } = useQuery<
    ApiResponse<UsersListResponse>,
    Error,
    UsersListResponse
  >({
    queryKey: [
      QUERY_KEYS.GET_ALL_USERS,
      selectedClient?._id,
      effectiveBranchId,
      effectiveSubBranchId,
    ],
    queryFn: () =>
      handleGetAllUsersService({
        status: 'active',
        clientId: selectedClient?._id,
        ...(effectiveBranchId.length > 0 && { branchId: effectiveBranchId }),
        ...(effectiveSubBranchId.length > 0 && {
          subBranchId: effectiveSubBranchId,
        }),
      }),
    enabled: !!selectedClient?._id,
  });

  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    (allClients ?? []).forEach((client: any) => {
      map.set(client.id, client.name);
      (client.branches ?? []).forEach((branch: any) => {
        map.set(branch.id, branch.name);
        (branch.subBranches ?? []).forEach((subBranch: any) => {
          map.set(subBranch.id, subBranch.name);
        });
      });
    });
    return map;
  }, [allClients]);

  const resolveLocationId = (
    entries: any[] | undefined,
    preferredIds: string[],
  ) => {
    const ids =
      entries
        ?.map((entry) => (typeof entry === 'string' ? entry : entry?._id))
        .filter(Boolean) ?? [];
    return (
      ids.find((id) => preferredIds.includes(id)) ??
      ids.find((id) => locationNameById.has(id))
    );
  };

  const getDeptGroupLabel = (department: any) => {
    const subBranchId = resolveLocationId(
      department.subBranchId,
      effectiveSubBranchId,
    );
    if (subBranchId) return locationNameById.get(subBranchId) ?? '';

    const branchId = resolveLocationId(department.branchId, effectiveBranchId);
    if (branchId) return locationNameById.get(branchId) ?? '';

    const clientId = resolveLocationId(
      department.clientId,
      selectedClient?._id ? [selectedClient._id] : [],
    );
    return clientId ? (locationNameById.get(clientId) ?? '') : '';
  };

  const departmentOptions = useMemo(() => {
    if (allDepartments && allDepartments?.departments?.length > 0) {
      const departmentsByLocation = allDepartments.departments
        .map((department: any) => ({
          label: department.name,
          value: department._id,
          icon: department.icon.startsWith('icon')
            ? ICONS.find((icon) => icon.id === department.icon)?.icon || ''
            : getCorrectPath(department.icon, 'image'),
          groupLabel: getDeptGroupLabel(department),
        }))
        .sort((a: { groupLabel: string }, b: { groupLabel: string }) =>
          a.groupLabel.localeCompare(b.groupLabel),
        );

      return [
        {
          label: t('forms.dialog.settings.allDepartments'),
          value: 'all',
          icon: SVG.saveAllIcon,
          groupLabel: '',
        },
        ...departmentsByLocation,
      ];
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allDepartments,
    t,
    locationNameById,
    selectedClient?._id,
    effectiveBranchKey,
    effectiveSubBranchKey,
  ]);

  // Local UI state to properly show all checkboxes ticked when "all" is selected
  const [localDeptUI, setLocalDeptUI] = useState<string[]>([]);
  const departmentIds = watch('settings.departmentIds');

  const branchOptions = (allClients ?? [])
    .filter((c) => c.id === selectedClient?._id)
    .filter((c) => (c.branches?.length ?? 0) > 0) as ClientDataType[];

  const selectedBranchHasSubBranches = useMemo(() => {
    if (!selectedLocation?.isBranch || !selectedLocation.id) return false;
    const client = branchOptions[0];
    const branch = client?.branches?.find((b) => b.id === selectedLocation.id);
    return (branch?.subBranches?.length ?? 0) > 0;
  }, [branchOptions, selectedLocation]);

  const displayBranchTree =
    (selectedLocation?.isClient && branchOptions.length > 0) ||
    (selectedLocation?.isBranch && selectedBranchHasSubBranches);

  useEffect(() => {
    const totalDepts = allDepartments?.departments?.length || 0;
    const setLocalDeptUIIfChanged = (next: string[]) => {
      const isSame =
        localDeptUI.length === next.length &&
        localDeptUI.every((value, index) => value === next[index]);
      if (!isSame) setLocalDeptUI(next);
    };

    if (departmentIds === 'all') {
      setLocalDeptUIIfChanged(departmentOptions.map((o) => o.value));
    } else if (
      Array.isArray(departmentIds) &&
      totalDepts > 0 &&
      departmentIds.length === totalDepts
    ) {
      // All individual departments selected → upgrade to "all"
      setValue('settings.departmentIds', 'all', { shouldDirty: true });
      setLocalDeptUIIfChanged(departmentOptions.map((o) => o.value));
    } else if (Array.isArray(departmentIds) && departmentIds.length > 0) {
      setLocalDeptUIIfChanged(departmentIds);
    } else {
      setLocalDeptUIIfChanged([]);
    }
  }, [departmentIds, departmentOptions, allDepartments, localDeptUI, setValue]);

  const handleDepartmentsChange = (
    _e: React.SyntheticEvent,
    newValue: { value: string }[] | null,
  ) => {
    const selectedValues = newValue?.map((item) => item.value) || [];
    const currentDepartments = getValues('settings.departmentIds');
    const hadAllPreviously =
      currentDepartments === 'all' ||
      (Array.isArray(currentDepartments) && currentDepartments.includes('all'));
    const hasAllInSelection = selectedValues.includes('all');
    const allDeptSelected = allDepartments?.departments?.every((dept: any) =>
      selectedValues.includes(dept._id),
    );

    let newDeptValue: string[] | 'all';

    if (hadAllPreviously && !hasAllInSelection) {
      // User deselected "All" → clear everything
      newDeptValue = [];
    } else if (hadAllPreviously && hasAllInSelection) {
      // User clicked an individual dept while "All" was active → remove "all", keep the rest
      newDeptValue = selectedValues.filter((v) => v !== 'all');
    } else if (hasAllInSelection || allDeptSelected) {
      // User selected "All" or selected every individual dept
      newDeptValue = 'all';
    } else {
      newDeptValue = selectedValues;
    }

    setValue('settings.departmentIds', newDeptValue, { shouldDirty: true });

    // Update UI state
    if (newDeptValue === 'all') {
      setLocalDeptUI(departmentOptions.map((o) => o.value));
    } else {
      setLocalDeptUI(newDeptValue);
    }
  };

  const userOptions = useMemo(() => {
    if (allUsers && allUsers?.data?.length > 0) {
      const uniqueUsers = allUsers.data.filter(
        (user: any, index: number, self: any[]) =>
          index === self.findIndex((u: any) => u._id === user._id),
      );

      return uniqueUsers.map((user: any) => ({
        value: user._id,
        title: `${user.firstName} ${user.lastName}`,
        role: user.roles,
      }));
    }
    return [];
  }, [allUsers]);

  const timingTypeOptions = TIMING_TYPES.map((type) => ({
    value: type.value,
    label: isHebrew ? type.labelHe : type.labelEn,
  }));

  const settings = watch('settings');

  const SWITCHES = [
    {
      name: 'allQuestionsRequired' as const,
      label: t('forms.dialog.settings.allRequired'),
    },
    {
      name: 'countRespondents' as const,
      label: t('forms.dialog.settings.countRespondents'),
    },
    {
      name: 'anonymousForm' as const,
      label: t('forms.dialog.settings.anonymous'),
    },
    {
      name: 'formForCustomer' as const,
      label: t('forms.dialog.settings.forCustomer'),
    },
  ];

  const schedulingType = settings.schedulingType;

  const handleSchedulingTypeChange = (key: string) => {
    setValue('settings.schedulingType', key, { shouldDirty: true });
    clearCustomTimingData();

    // Reset fields
    setValue('settings.startDate', moment().format('YYYY-MM-DD'), {
      shouldDirty: true,
    });
    setValue('settings.endDate', moment().format('YYYY-MM-DD'), {
      shouldDirty: true,
    });
    setValue('settings.startTime', '', { shouldDirty: true });
    setValue('settings.endTime', '', { shouldDirty: true });
    setValue('settings.time', '', { shouldDirty: true });
    setValue('settings.dayOfWeek', 0, { shouldDirty: true });
    setValue('settings.customTiming', null, { shouldDirty: true });

    // Per-type adjustments
    if (key === 'all_day' || key === 'by_day_and_time') {
      setValue('settings.endDate', '', { shouldDirty: true });
    }

    if (key === 'daily_at_fixed_time' || key === 'custom') {
      setValue('settings.startDate', '', { shouldDirty: true });
      setValue('settings.endDate', '', { shouldDirty: true });
    }

    if (key === 'custom') {
      setShowCustomTimingDialog(true);
    }
  };

  const isFieldVisible = (fieldName: string) => {
    switch (fieldName) {
      case 'startDate':
        return (
          schedulingType === 'default' ||
          schedulingType === 'all_day' ||
          schedulingType === 'by_day_and_time' ||
          schedulingType === 'between_days_and_hours'
        );
      case 'endDate':
        return (
          schedulingType === 'default' ||
          schedulingType === 'between_days_and_hours'
        );
      case 'startTime':
      case 'endTime':
        return (
          schedulingType === 'by_day_and_time' ||
          schedulingType === 'between_days_and_hours' ||
          schedulingType === 'daily_at_fixed_time' ||
          schedulingType === 'custom'
        );
      default:
        return true;
    }
  };

  const formattedDate = (endDate: string | Date) =>
    new Date(endDate).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const customEndLabel = () => {
    switch (customTimingData?.endCondition) {
      case 'never':
        return 'ממשיך ללא סיום';
      case 'end_date':
        return `מסתיים ${formattedDate(customTimingData?.endDate || '')}`;
      case 'occurrences':
        return `מסיים לאחר ${customTimingData?.occurrences} הופעות`;
      default:
        return '';
    }
  };

  return (
    <>
      <div className="flex flex-col h-full gap-6 py-4 overflow-y-auto px-4">
        <h2 className="text-[32px] font-normal text-black">
          {t('forms.dialog.settings.title')}
        </h2>

        {/* General Section */}
        <div className="flex flex-col gap-4">
          <h3 className="text-xl font-bold text-darkerGray">
            {t('forms.dialog.settings.general')}
          </h3>

          {SWITCHES.map((switchItem) => (
            <div key={switchItem.name} className="flex items-center gap-2">
              <Controller
                name={`settings.${switchItem.name}`}
                control={control}
                render={({ field }) => (
                  <AppSwitch
                    checked={field.value}
                    onChange={(checked) => {
                      // Only mark manual toggle for allQuestionsRequired
                      if (switchItem.name === 'allQuestionsRequired') {
                        const questions = getValues('questions') || [];

                        setValue(
                          'questions',
                          questions.map((q) => ({
                            ...q,
                            mandatory: checked.target.checked,
                          })),
                          { shouldDirty: true },
                        );
                      }

                      field.onChange(checked);
                    }}
                  />
                )}
              />
              <span className="text-black">{switchItem.label}</span>
            </div>
          ))}
        </div>

        {/* Recipients Section */}
        <div className="flex flex-col gap-4 pt-4 border-t border-gray80">
          <div className="flex items-center gap-2">
            <img
              src={SVG.usersActiveIcon}
              alt="settings"
              className="w-[24px] h-[24px] object-contain"
            />

            <h3 className="text-xl font-bold text-darkerGray">
              {t('forms.dialog.settings.recipientsTitle')}
            </h3>
          </div>

          <div className="flex items-center gap-2">
            {/* Copy Mode: Client + Branch dropdowns for target location */}
            {/* {copyMode ? (
              <>
                <div className="flex-1">
                  <AppMUIAutocomplete
                    label="בית עסק"
                    size="small"
                    fullWidth
                    options={allClients || []}
                    getOptionLabel={(option: any) => option.name}
                    value={
                      allClients?.find((c: any) => c.id === copyClientId) ||
                      null
                    }
                    onChange={(_e: any, val: any) => {
                      setCopyClientId?.(val?.id || '');
                      setCopyBranchIds?.([]);
                    }}
                  />
                </div>
                <div className="flex-1">
                  <AppMUIAutocomplete
                    label="סניף"
                    size="small"
                    fullWidth
                    options={
                      allClients?.find((c: any) => c.id === copyClientId)
                        ?.branches || []
                    }
                    getOptionLabel={(option: any) => option?.name}
                    disabled={!copyClientId}
                    value={
                      allClients
                        ?.find((c: any) => c.id === copyClientId)
                        ?.branches?.find(
                          (b: any) => b.id === copyBranchIds?.[0],
                        ) || null
                    }
                    onChange={(_e: any, val: any) => {
                      setCopyBranchIds?.(val?.id ? [val.id] : []);
                    }}
                  />
                </div>
              </>
            )} */}
            {displayBranchTree && (
              <div className="flex-1">
                <Controller
                  name="settings.branchIds"
                  control={control}
                  render={({ field: { value: branchIdsValue } }) => {
                    return (
                      <BranchTreeAutocomplete
                        label={t('forms.dialog.settings.organization')}
                        showClientChip
                        allClients={branchOptions}
                        value={[
                          ...(branchIdsValue || []),
                          ...(subBranchIds || []),
                        ]}
                        onChange={(nodes) => {
                          const prevBranchIds =
                            getValues('settings.branchIds') || [];

                          const newBranchIds =
                            nodes
                              ?.filter((item) => item?.isBranch)
                              ?.map((item) => item?.id) || [];
                          const newSubBranches = (nodes?.filter(
                            (item) => item?.isSubBranch,
                          ) || []) as SubBranchData[];

                          const removedBranchIds = prevBranchIds.filter(
                            (id) => !newBranchIds.includes(id),
                          );

                          const filteredSubBranches = newSubBranches.filter(
                            (sub) => !removedBranchIds.includes(sub.branchId),
                          );

                          const autoAddBranchIds = filteredSubBranches
                            .filter(
                              (sub) => !newBranchIds.includes(sub.branchId),
                            )
                            .map((sub) => sub.branchId);

                          const finalBranchIds = Array.from(
                            new Set([...newBranchIds, ...autoAddBranchIds]),
                          );
                          const finalSubBranchIds = filteredSubBranches.map(
                            (sub) => sub.id,
                          );

                          setValue('settings.branchIds', finalBranchIds, {
                            shouldDirty: true,
                          });
                          setValue('settings.subBranchIds', finalSubBranchIds, {
                            shouldDirty: true,
                          });

                          setValue('settings.departmentIds', [], {
                            shouldDirty: true,
                          });
                          setValue('settings.recipientIds', [], {
                            shouldDirty: true,
                          });
                        }}
                      />
                    );
                  }}
                />
              </div>
            )}

            {/* Departments */}
            <div className="flex-1">
              <Controller
                name="settings.departmentIds"
                control={control}
                render={() => {
                  const allSelected =
                    localDeptUI.includes('all') ||
                    localDeptUI.length === departmentOptions.length;

                  return (
                    <MUIIconCheckboxSelect
                      label={t('forms.dialog.settings.departments')}
                      options={departmentOptions}
                      groupBy={(option) => option.groupLabel || ''}
                      size="small"
                      fullWidth
                      disableCloseOnSelect
                      value={departmentOptions.filter((opt) =>
                        localDeptUI.includes(opt.value),
                      )}
                      onChange={handleDepartmentsChange}
                      renderValue={(selectedOptions, getTagProps) => {
                        if (allSelected) {
                          const allOption = departmentOptions.find(
                            (o) => o.value === 'all',
                          );
                          return allOption ? (
                            <Chip
                              label={allOption.label}
                              icon={
                                <img
                                  src={allOption.icon}
                                  alt="icon"
                                  className="w-[18px] h-[18px] object-contain"
                                />
                              }
                              {...getTagProps({ index: 0 })}
                            />
                          ) : null;
                        }
                        return selectedOptions.map(
                          (option: any, index: number) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            return (
                              <Chip
                                key={option.value}
                                label={option.label}
                                icon={
                                  <img
                                    src={option.icon}
                                    alt="icon"
                                    className="w-[18px] h-[18px] object-contain"
                                  />
                                }
                                {...tagProps}
                              />
                            );
                          },
                        );
                      }}
                    />
                  );
                }}
              />
            </div>

            {/* Recipients */}
            <div className="flex-1">
              <Controller
                name="settings.recipientIds"
                control={control}
                render={({ field }) => (
                  <AppMUIAutocomplete
                    label={t('forms.dialog.settings.recipients')}
                    size="small"
                    fullWidth
                    multiple
                    disableCloseOnSelect
                    options={userOptions}
                    slotProps={{ listbox: { sx: { maxHeight: 300 } } }}
                    getOptionLabel={(option: any) => option.title}
                    renderOption={(props, option: any) => {
                      const { key, ...optionProps } = props;
                      const bgColor = option?.role?.[0]?.color || '#E0E0E0';

                      return (
                        <li
                          key={option.value}
                          {...optionProps}
                          className="flex flex-col"
                        >
                          <div className="flex">
                            <div
                              className="w-[32px] h-[32px] rounded-full me-2 flex items-center justify-center"
                              style={{ backgroundColor: bgColor }}
                            >
                              <span className="text-xl text-black">
                                {option.title.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <span>{option.title}</span>
                              <div className="flex flex-wrap gap-2 mt-1 mb-3">
                                {option.role && option.role.length > 0 && (
                                  <span
                                    className="text-xs text-grayText px-2 py-0.5 rounded-full flex items-center gap-1"
                                    style={{ backgroundColor: bgColor }}
                                  >
                                    <img
                                      src={
                                        ICONS.find(
                                          (item: any) =>
                                            item.id ===
                                            (option?.role?.[0]?.roleType
                                              ?.icon ||
                                              option?.role?.[0]?.icon),
                                        )?.icon
                                      }
                                      alt=""
                                      className="h-3 w-3"
                                    />
                                    <span>
                                      {getRoleTypeName(
                                        option?.role?.[0]?.roleType,
                                      ) || ''}
                                    </span>
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    }}
                    value={
                      userOptions.filter((item: any) =>
                        (field.value as string[])?.includes(item.value),
                      ) || []
                    }
                    onChange={(_, newValue) => {
                      const values = Array.isArray(newValue)
                        ? newValue.map((item: any) => item.value)
                        : [];
                      field.onChange(values);
                    }}
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* Timing Section */}
        <div className="flex flex-col gap-3 pt-4 border-t border-gray80">
          <div className="flex items-center gap-2">
            <img
              src={SVG.clockIcon}
              alt="settings"
              className="w-[20px] h-[20px] object-contain"
            />

            <h3 className="text-xl font-bold text-darkerGray">
              {t('forms.dialog.settings.timing')}
            </h3>
          </div>

          <p className="m-0 text-darkerGray">
            {t('forms.dialog.settings.timingDescription')}
          </p>

          <div className="flex flex-col gap-4">
            {timingTypeOptions.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-3 cursor-pointer"
              >
                <input
                  type="radio"
                  name="timingType"
                  checked={settings.timingType === option.value}
                  onChange={() =>
                    setValue(
                      'settings.timingType',
                      option.value as 'single' | 'scheduled',
                      {
                        shouldDirty: true,
                      },
                    )
                  }
                  className="w-5 h-5 accent-darkBlue cursor-pointer"
                />
                <span
                  className={`text-black ${settings.timingType === option.value && 'font-semibold'}`}
                >
                  {option.label}
                </span>
              </label>
            ))}
          </div>

          {settings.timingType === 'scheduled' && (
            <div className="flex flex-col gap-3 max-w-[743px]">
              {/* Scheduling Type Dropdown */}
              <div className="flex items-center gap-2">
                <img
                  src={SVG.clockIcon}
                  alt="settings"
                  className="w-[20px] h-[20px] object-contain"
                />

                <Controller
                  control={control}
                  name="settings.schedulingType"
                  render={({ field }) => (
                    <AppMUIAutocomplete
                      label="תזמון מותאם"
                      size="small"
                      options={SCHEDULING_TYPES}
                      sx={{
                        flex: 1,
                        minWidth: '260px',
                        '& .MuiAutocomplete-popupIndicator': {
                          transform: 'none !important',
                        },
                      }}
                      popupIcon={<CalendarMonthOutlinedIcon />}
                      getOptionLabel={(option) => option.title}
                      value={
                        SCHEDULING_TYPES.find(
                          (item) => item.key === field.value,
                        ) || null
                      }
                      onChange={(_, newValue) => {
                        const key = newValue?.key || 'default';
                        handleSchedulingTypeChange(key);
                      }}
                    />
                  )}
                />
              </div>

              {/* Date Fields */}
              <div className="flex items-start gap-2">
                {isFieldVisible('startDate') && (
                  <Controller
                    control={control}
                    name="settings.startDate"
                    render={({ field }) => (
                      <DateInput
                        dateFormat="EEEE, d 'ב'MMMM"
                        placeholderText="yyyy, mmm, dd"
                        selected={field.value ? new Date(field.value) : null}
                        onChange={(date) =>
                          field.onChange(date?.toISOString() || '')
                        }
                        label={
                          schedulingType === 'all_day'
                            ? 'יום'
                            : schedulingType === 'by_day_and_time'
                              ? 'ביום'
                              : 'מ-'
                        }
                      />
                    )}
                  />
                )}

                {isFieldVisible('endDate') && (
                  <Controller
                    control={control}
                    name="settings.endDate"
                    render={({ field }) => (
                      <DateInput
                        dateFormat="EEEE, d 'ב'MMMM"
                        placeholderText="yyyy, mmm, dd"
                        selected={field.value ? new Date(field.value) : null}
                        onChange={(date) =>
                          field.onChange(date?.toISOString() || '')
                        }
                        label="עד-"
                      />
                    )}
                  />
                )}
              </div>

              {/* Time Fields — by_day_and_time / between_days_and_hours / daily_at_fixed_time */}
              {(schedulingType === 'by_day_and_time' ||
                schedulingType === 'between_days_and_hours' ||
                schedulingType === 'daily_at_fixed_time') && (
                <div className="flex flex-row items-start gap-2">
                  <Controller
                    control={control}
                    name="settings.startTime"
                    render={({ field }) => (
                      <AppMUIAutocomplete
                        freeSolo
                        disableClearable
                        options={TIMES.map((i) => i.title)}
                        label="משעה"
                        className="w-full"
                        size="small"
                        value={field.value}
                        onChange={(_, v) =>
                          setValue('settings.startTime', v as string, {
                            shouldDirty: true,
                          })
                        }
                        error={!!errors?.settings?.startTime?.message}
                        helperText={errors?.settings?.startTime?.message}
                        onInputChange={(_e, newInputValue, reason) => {
                          if (reason === 'input') {
                            const matchingType = TIMES?.find(
                              (item: any) => item?.title === newInputValue,
                            );
                            if (matchingType) {
                              setValue(
                                'settings.startTime',
                                matchingType.value,
                                {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                },
                              );
                            } else {
                              setValue('settings.startTime', newInputValue, {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                            }
                          }
                        }}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="settings.endTime"
                    render={({ field }) => (
                      <AppMUIAutocomplete
                        freeSolo
                        disableClearable
                        options={TIMES.map((i) => i.title)}
                        label="לשעה"
                        className="w-full"
                        size="small"
                        value={field.value}
                        onChange={(_, v) =>
                          setValue('settings.endTime', v as string, {
                            shouldDirty: true,
                          })
                        }
                        error={!!errors?.settings?.endTime?.message}
                        helperText={errors?.settings?.endTime?.message}
                        onInputChange={(_e, newInputValue, reason) => {
                          if (reason === 'input') {
                            const matchingType = TIMES?.find(
                              (item: any) => item?.title === newInputValue,
                            );
                            if (matchingType) {
                              setValue('settings.endTime', matchingType.value, {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                            } else {
                              setValue('settings.endTime', newInputValue, {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                            }
                          }
                        }}
                      />
                    )}
                  />
                </div>
              )}

              {/* Custom Timing */}
              {schedulingType === 'custom' && (
                <div className="flex flex-col gap-3">
                  <div className="flex items-start gap-2">
                    <Controller
                      control={control}
                      name="settings.startTime"
                      render={({ field }) => (
                        <AppMUIAutocomplete
                          freeSolo
                          disableClearable
                          options={TIMES.map((i) => i.title)}
                          label="משעה"
                          className="w-full"
                          size="small"
                          value={field.value}
                          onChange={(_, v) =>
                            setValue('settings.startTime', v as string, {
                              shouldDirty: true,
                            })
                          }
                          error={!!errors?.settings?.startTime?.message}
                          helperText={errors?.settings?.startTime?.message}
                          onInputChange={(_e, newInputValue, reason) => {
                            if (reason === 'input') {
                              const matchingType = TIMES?.find(
                                (item: any) => item?.title === newInputValue,
                              );
                              if (matchingType) {
                                setValue(
                                  'settings.startTime',
                                  matchingType.value,
                                  {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  },
                                );
                              } else {
                                setValue('settings.startTime', newInputValue, {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                });
                              }
                            }
                          }}
                        />
                      )}
                    />

                    <Controller
                      control={control}
                      name="settings.endTime"
                      render={({ field }) => (
                        <AppMUIAutocomplete
                          freeSolo
                          disableClearable
                          options={TIMES.map((i) => i.title)}
                          label="לשעה"
                          className="w-full"
                          size="small"
                          value={field.value}
                          onChange={(_, v) =>
                            setValue('settings.endTime', v as string, {
                              shouldDirty: true,
                            })
                          }
                          error={!!errors?.settings?.endTime?.message}
                          helperText={errors?.settings?.endTime?.message}
                          onInputChange={(_e, newInputValue, reason) => {
                            if (reason === 'input') {
                              const matchingType = TIMES?.find(
                                (item: any) => item?.title === newInputValue,
                              );
                              if (matchingType) {
                                setValue(
                                  'settings.endTime',
                                  matchingType.value,
                                  {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  },
                                );
                              } else {
                                setValue('settings.endTime', newInputValue, {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                });
                              }
                            }
                          }}
                        />
                      )}
                    />

                    <AppMUIAutocomplete
                      options={DAYS}
                      label="יום"
                      className="w-full"
                      size="small"
                      getOptionLabel={(option) => option.label}
                      multiple
                      value={
                        customTimingData?.weeklyDays &&
                        customTimingData.weeklyDays.length > 0
                          ? DAYS.filter((day) =>
                              customTimingData.weeklyDays.includes(day.index),
                            ) || []
                          : []
                      }
                      onChange={(_, newValue) => {
                        const updatedData: CustomTimingFormValues = {
                          repeatEvery: customTimingData?.repeatEvery || 1,
                          repeatType: customTimingData?.repeatType || 'weekly',
                          monthlyDates: customTimingData?.monthlyDates || [],
                          endCondition:
                            customTimingData?.endCondition || 'never',
                          weeklyDays:
                            newValue?.length > 0
                              ? newValue.map((day) => day.index)
                              : [],
                          ...(customTimingData?.endDate && {
                            endDate: customTimingData.endDate,
                          }),
                          ...(customTimingData?.occurrences && {
                            occurrences: customTimingData.occurrences,
                          }),
                        };
                        setCustomTimingData(updatedData);
                      }}
                    />
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex flex-col gap-1">
                      {customTimingData?.repeatEvery && (
                        <span className="text-xl font-medium">
                          חוזר על עצמו כל {customTimingData.repeatEvery}{' '}
                          {customTimingData?.repeatType === 'weekly'
                            ? 'שבועות'
                            : 'חודשים'}
                        </span>
                      )}

                      {customTimingData?.endCondition && (
                        <span className="text-xl font-medium">
                          {customEndLabel()}
                        </span>
                      )}
                    </div>

                    <div className="ml-auto">
                      <AppButton
                        onClick={() => setShowCustomTimingDialog(true)}
                        style={{ paddingInline: 15, paddingBlock: 6 }}
                        variant="custom"
                        className="bg-white border border-[#CAC4D0] text-[#007AFF] text-[14px] font-medium rounded-[8px]"
                      >
                        לעדכון פרטים
                      </AppButton>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Confirmation Text */}
        <div className="flex flex-col gap-3 pt-4 border-t border-gray80">
          <h3 className="text-xl font-bold text-darkerGray">
            {t('forms.dialog.settings.confirmationTitle')}
          </h3>

          <Controller
            name="settings.confirmationText"
            control={control}
            render={({ field }) => (
              <AppTextInput
                {...field}
                placeholder={t('forms.dialog.settings.confirmationPlaceholder')}
                className="border-none"
              />
            )}
          />
        </div>
      </div>

      {/* Custom Timing Dialog */}
      <CustomTiming
        open={showCustomTimingDialog}
        onClose={() => setShowCustomTimingDialog(false)}
        onCancel={() => {
          setShowCustomTimingDialog(false);
          clearCustomTimingData();
          handleSchedulingTypeChange('default');
        }}
        onSubmit={(data) => {
          setValue('settings.customTiming', data as any, { shouldDirty: true });
          setShowCustomTimingDialog(false);
        }}
      />
    </>
  );
}

export default SettingsTab;
